import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { Building2, CheckCircle2, QrCode, Package } from 'lucide-react';
import ChefHeader from '../shared/ui/ChefHeader';
import MapView from '../shared/MapView';
import { Card, EmptyState, LoadingView, SegmentedPicker, StatusBadge } from '../shared/ui';
import { db, storage } from '../shared/firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  activeDeliveries,
  listenToAcceptedOrder,
  listenToAcceptedOrders,
  updateDriverOrderStatus,
} from '../shared/services/orderService';
import { fetchUserProfile } from '../shared/services/userService';
import useRoadRoute from '../shared/useRoadRoute';
import {
  DELIVERY_PROXIMITY_FEET,
  DriverOrderStatus,
  distanceFeet,
  formatCurrency,
  formatTime,
} from '../shared/models';
import { useDriverLocation } from '../components/work/useDriverLocation';
import { maybeUpdateOrderETA } from '../shared/eta';
import { customerCoords, deliveryAddressText, navLinks, restaurantCoords } from '../components/work/geo';
import NavButtons from '../components/work/NavButtons';
import InfoAlert from '../components/work/InfoAlert';
import WorkflowSteps, {
  NEXT_ACTION_LABEL,
  NEXT_STATUS,
  stepState,
} from '../components/delivery/WorkflowSteps';
import CustomerCard from '../components/delivery/CustomerCard';
import ScanSheet from '../components/delivery/ScanSheet';
import ConfirmDeliverySheet from '../components/delivery/ConfirmDeliverySheet';

const PRE_PICKUP = new Set([
  DriverOrderStatus.ACCEPTED,
  DriverOrderStatus.NAVIGATE_TO_RESTAURANT,
  DriverOrderStatus.ARRIVED_AT_RESTAURANT,
]);

function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('Geolocation is not supported by this browser.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 5000,
    });
  });
}

export default function DeliveryDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { driver, driverId } = useAuth();

  const [order, setOrder] = useState(undefined); // undefined=loading, null=gone
  const [accepted, setAccepted] = useState([]);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [showScan, setShowScan] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [verified, setVerified] = useState(false);
  const [busy, setBusy] = useState(false);
  const [alert, setAlert] = useState(null);
  const hadOrderRef = useRef(false);
  const lastOrderRef = useRef(null);

  // Live accepted-order doc (deleted by the service once Delivered).
  useEffect(() => {
    if (!driverId || !orderId) return undefined;
    return listenToAcceptedOrder(driverId, orderId, (o) => {
      setOrder(o);
      if (o) {
        hadOrderRef.current = true;
        lastOrderRef.current = o;
      }
    });
  }, [driverId, orderId]);

  // All accepted orders → powers the 2-delivery switcher.
  useEffect(() => {
    if (!driverId) return undefined;
    return listenToAcceptedOrders(driverId, setAccepted);
  }, [driverId]);

  // Customer profile (photo) from users/{customerId}.
  useEffect(() => {
    if (!order?.customerId) return;
    let cancelled = false;
    fetchUserProfile(order.customerId)
      .then((p) => !cancelled && setCustomerProfile(p))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [order?.customerId]);

  // Keep the location stream pointed at this delivery while viewing it.
  const { position } = useDriverLocation(driverId, !!driver?.isOnline, orderId);

  // iOS ETAManager parity — refresh this order's ETA as the driver moves.
  useEffect(() => {
    if (driver?.isOnline && position && order) {
      maybeUpdateOrderETA(order, position, driverId);
    }
  }, [position, order, driver?.isOnline, driverId]);

  const active = useMemo(() => {
    return activeDeliveries(accepted).sort(
      (a, b) => (a.acceptedAt?.getTime?.() || 0) - (b.acceptedAt?.getTime?.() || 0)
    );
  }, [accepted]);

  // Hooks must run before the early returns below — null-safe when the
  // order hasn't loaded. Road-following routed line (Google-style), with
  // a straight segment as the fallback.
  const pickup = restaurantCoords(order);
  const dropoff = customerCoords(order);
  const route = useRoadRoute(pickup, dropoff);

  async function doUpdate(status, extra = {}) {
    if (busy) return;
    setBusy(true);
    try {
      await updateDriverOrderStatus(driverId, order.orderId, status, extra);
    } catch (e) {
      setAlert({ title: 'Update Failed', message: e.message || 'Could not update the order.' });
    } finally {
      setBusy(false);
    }
  }

  function openNav(coords) {
    const links = navLinks(coords);
    if (links) window.open(links.google, '_blank', 'noopener');
  }

  // Per-step tap actions (mirrors iOS OrderCompleteCardView.handleStepAction).
  // Only the entry pair (navigate / arrived at restaurant) or the single next
  // step can run — later steps stay locked until the one before completes,
  // so the QR scan can't be skipped.
  function handleStepTap(_status, idx) {
    const { completed } = stepState(order.status);
    if (idx < completed || (idx > 1 && idx !== completed)) return;
    switch (idx) {
      case 0:
        openNav(restaurantCoords(order));
        break;
      case 1:
        doUpdate(DriverOrderStatus.ARRIVED_AT_RESTAURANT);
        break;
      case 2:
        // Pickup requires scanning the order's receipt QR / order ID —
        // validateOrderQRCode marks it pickedUpOrder server-side.
        if (verified) {
          doUpdate(DriverOrderStatus.PICKED_UP_ORDER);
        } else {
          setShowScan(true);
        }
        break;
      case 3:
        doUpdate(DriverOrderStatus.ON_MY_WAY);
        break;
      case 4:
        doUpdate(DriverOrderStatus.ARRIVED_AT_CUSTOMER);
        break;
      case 5:
        setShowConfirm(true);
        break;
      default:
        break;
    }
  }

  async function advance() {
    const next = NEXT_STATUS[order.status];
    if (!next) return;
    if (next === DriverOrderStatus.DELIVERED) {
      setShowConfirm(true);
      return;
    }
    // Pickup is gated on the receipt scan — the driver must verify they
    // have the right order before "On My Way" unlocks.
    if (next === DriverOrderStatus.PICKED_UP_ORDER && !verified) {
      setShowScan(true);
      return;
    }
    await doUpdate(next);
  }

  // Proximity gate + optional photo upload + final status update.
  async function confirmDelivery(file) {
    const dest = customerCoords(order);
    if (!dest) {
      throw new Error('Delivery location is unavailable — cannot verify proximity.');
    }
    let pos;
    try {
      pos = await getCurrentPosition();
    } catch {
      throw new Error(
        'Unable to determine your location. Please enable location services and try again.'
      );
    }
    const d = distanceFeet(
      { lat: Number(pos.coords.latitude), lng: Number(pos.coords.longitude) },
      dest
    );
    // Fail closed: null/NaN distance means we can't prove proximity —
    // iOS blocks the same way when it can't compute a valid distance.
    if (!Number.isFinite(d) || d > DELIVERY_PROXIMITY_FEET) {
      throw new Error(
        `You must be within ${DELIVERY_PROXIMITY_FEET} feet of the delivery address to complete this order. You are currently ${d ? Math.round(d) : 'an unknown distance'} feet away.`
      );
    }

    let imageURL = null;
    if (file) {
      // iOS DeliveryConfirmationView: storage path delivery-images/{orderId}/{uuid}.jpg
      const storagePath = `delivery-images/${order.orderId}/${crypto.randomUUID ? crypto.randomUUID() : Date.now()}.jpg`;
      const r = storageRef(storage, storagePath);
      await uploadBytes(r, file, { contentType: file.type || 'image/jpeg' });
      imageURL = await getDownloadURL(r);
      // iOS deliveryImages doc contract — the customer app reads
      // data["arrivalImageUrl"] off the driver_arrived notification and the
      // deliveryImages collection for the photo record.
      try {
        const imageRef = doc(collection(db, 'deliveryImages'));
        await setDoc(imageRef, {
          id: imageRef.id,
          orderId: order.orderId,
          imageUrl: imageURL,
          storagePath,
          uploadedBy: 'driver',
          uploadDate: serverTimestamp(),
          imageType: 'arrival_confirmation',
        });
      } catch (e) {
        console.warn('deliveryImages doc create denied/failed (non-fatal):', e);
      }
    }

    // iOS attaches the photo at the ARRIVAL step (driver_arrived notification
    // carries data.arrivalImageUrl). If the driver jumped straight to confirm
    // without tapping "Arrived at Customer", fire that step with the photo so
    // the customer gets the doorstep picture exactly like iOS.
    if (imageURL && order.status !== DriverOrderStatus.ARRIVED_AT_CUSTOMER) {
      await updateDriverOrderStatus(
        driverId,
        order.orderId,
        DriverOrderStatus.ARRIVED_AT_CUSTOMER,
        { arrivalImageUrl: imageURL }
      );
    }

    await updateDriverOrderStatus(
      driverId,
      order.orderId,
      DriverOrderStatus.DELIVERED,
      imageURL ? { deliveryImageURL: imageURL, arrivalImageUrl: imageURL } : {}
    );
  }

  const done = (
    <button
      onClick={() => navigate('/driver/work')}
      className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700"
    >
      Done
    </button>
  );

  // ---------- Loading / finished / missing states ----------
  if (order === undefined) {
    return (
      <div>
        <ChefHeader title="Delivery" right={done} />
        <LoadingView label="Loading delivery…" />
      </div>
    );
  }

  if (order === null) {
    const last = lastOrderRef.current;
    return (
      <div>
        <ChefHeader title="Delivery" right={done} />
        {hadOrderRef.current ? (
          <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
            <CheckCircle2 size={72} className="text-green-500" />
            <p className="text-xl font-extrabold text-gray-900">Delivery Complete!</p>
            <p className="text-sm text-gray-500">
              Order #{String(orderId).slice(0, 8)} has been successfully delivered.
            </p>
            {last?.driverPayout != null && (
              <p className="text-base font-bold text-green-600">
                You earned {formatCurrency(last.driverPayout)}
              </p>
            )}
            <button
              onClick={() => navigate('/driver/work')}
              className="mt-3 w-full max-w-xs rounded-2xl bg-[#FF6B35] py-3 text-sm font-bold text-white"
            >
              Back to Work
            </button>
          </div>
        ) : (
          <EmptyState
            icon={Package}
            title="Delivery Not Found"
            subtitle="This delivery is no longer active."
            action={
              <button
                onClick={() => navigate('/driver/work')}
                className="mt-2 rounded-2xl bg-[#FF6B35] px-6 py-2.5 text-sm font-bold text-white"
              >
                Back to Work
              </button>
            }
          />
        )}
      </div>
    );
  }

  if (order.status === 'Cancelled') {
    return (
      <div>
        <ChefHeader title="Delivery" right={done} />
        <EmptyState
          icon={Package}
          title="Order Cancelled"
          subtitle="This order was cancelled."
          action={
            <button
              onClick={() => navigate('/driver/work')}
              className="mt-2 rounded-2xl bg-[#FF6B35] px-6 py-2.5 text-sm font-bold text-white"
            >
              Back to Work
            </button>
          }
        />
      </div>
    );
  }

  // ---------- Live delivery workflow ----------
  const num = String(order.orderId || order.id).slice(0, 8);
  const target = PRE_PICKUP.has(order.status) ? pickup || dropoff : dropoff || pickup;
  const payout = order.driverPayout ?? order.totalEarnings ?? 0;
  const { completed } = stepState(order.status);
  const canVerify = completed <= 2; // before "Picked Up Order" is done
  const next = NEXT_STATUS[order.status];
  const nextIsFinal = next === DriverOrderStatus.DELIVERED;

  const markers = [
    pickup && { ...pickup, kind: 'restaurant', popup: order.restaurantName || 'Pickup' },
    dropoff && { ...dropoff, kind: 'customer', popup: order.customerName || 'Customer' },
  ].filter(Boolean);

  const items = order.items || order.orderItems || [];

  return (
    <div className="min-h-full pb-4">
      <ChefHeader title="Delivery" right={done} />

      <div className="space-y-4 p-4">
        {/* 2-delivery switcher (mirrors MultiOrderCompleteCardView tabs) */}
        {active.length > 1 && (
          <SegmentedPicker
            options={active.map((o, i) => ({
              value: o.orderId || o.id,
              label: `Order ${i + 1}`,
            }))}
            value={orderId}
            onChange={(v) => navigate(`/driver/delivery/${v}`)}
          />
        )}

        {/* Main card — mirrors OrderCompleteCardView */}
        <Card className="space-y-4 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-extrabold text-gray-900">Order #{num}</p>
              <p className="truncate text-xs text-gray-500">{order.restaurantName}</p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <StatusBadge status={order.status} />
              <p className="text-sm font-extrabold text-green-600">
                {formatCurrency(payout)}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-2.5">
            <Building2 size={18} className="mt-0.5 shrink-0 text-[#FF6B35]" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-900">{order.restaurantName}</p>
              {order.estimatedPickupTime && (
                <p className="text-[11px] text-gray-400">
                  Pickup by {formatTime(order.estimatedPickupTime)}
                </p>
              )}
            </div>
          </div>

          <WorkflowSteps status={order.status} onStepTap={handleStepTap} disabled={busy} />
        </Card>

        {/* Big action button */}
        {next && (
          <button
            onClick={advance}
            disabled={busy}
            className={`w-full rounded-2xl py-3.5 text-sm font-bold text-white shadow-sm disabled:opacity-60 active:scale-[0.98] ${
              nextIsFinal ? 'bg-green-500' : 'bg-[#FF6B35]'
            }`}
          >
            {busy ? 'Updating…' : NEXT_ACTION_LABEL[order.status]}
          </button>
        )}

        {/* Verify order — required before pickup; the same sheet opens
            automatically when the driver taps "Picked Up Order". */}
        {canVerify &&
          (verified ? (
            <p className="flex items-center justify-center gap-1.5 rounded-2xl bg-green-50 py-3 text-sm font-bold text-green-600">
              <CheckCircle2 size={18} /> Order Verified
            </p>
          ) : (
            <button
              onClick={() => setShowScan(true)}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-[#FF6B35]/30 bg-white py-3 text-sm font-bold text-[#FF6B35] active:scale-[0.98]"
            >
              <QrCode size={18} /> Verify Order
            </button>
          ))}

        {/* Customer */}
        <CustomerCard order={order} profile={customerProfile} />

        {/* Address + route map */}
        <Card className="space-y-2 p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-400">
            {PRE_PICKUP.has(order.status) ? 'Head to Restaurant' : 'Head to Customer'}
          </p>
          {deliveryAddressText(order) && !PRE_PICKUP.has(order.status) && (
            <p className="text-xs text-gray-600">📍 {deliveryAddressText(order)}</p>
          )}
          <div className="h-56 overflow-hidden rounded-xl border border-gray-100">
            <MapView markers={markers} route={route} />
          </div>
          <NavButtons coords={target} compact />
        </Card>

        {/* Items */}
        <Card className="space-y-2 p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-400">
            Order Items ({items.length})
          </p>
          <div className="divide-y divide-gray-50">
            {items.map((it, i) => (
              <div key={it.id || i} className="flex items-center gap-2 py-1.5 text-xs">
                <span className="font-bold text-gray-400">×{it.quantity || 1}</span>
                <span className="flex-1 truncate text-gray-800">
                  {it.menuItemName || it.name || it.menuItem?.name || 'Item'}
                </span>
                <span className="text-gray-500">
                  {formatCurrency(it.totalPrice ?? (it.price ?? it.menuItem?.price ?? 0) * (it.quantity || 1))}
                </span>
              </div>
            ))}
          </div>
          <div className="space-y-0.5 border-t border-gray-100 pt-2 text-[11px] text-gray-500">
            <p className="flex justify-between">
              <span>Subtotal</span>
              <span>{formatCurrency(order.subtotal)}</span>
            </p>
            {order.tipAmount > 0 && (
              <p className="flex justify-between">
                <span>Tip</span>
                <span>{formatCurrency(order.tipAmount)}</span>
              </p>
            )}
            <p className="flex justify-between text-xs font-extrabold text-green-600">
              <span>Your Payout</span>
              <span>{formatCurrency(payout)}</span>
            </p>
          </div>
          {order.specialInstructions ? (
            <p className="rounded-xl bg-orange-50 p-2.5 text-[11px] text-[#FF6B35]">
              📝 {order.specialInstructions}
            </p>
          ) : null}
        </Card>
      </div>

      <ScanSheet
        open={showScan}
        onClose={() => setShowScan(false)}
        order={order}
        driverId={driverId}
        onVerified={() => {
          setVerified(true);
          setShowScan(false);
          // The callable already wrote status=pickedUpOrder on the order —
          // this syncs the driver's acceptedOrders doc + notifications,
          // mirroring iOS OrderCompleteCardView.handleScanSuccess.
          doUpdate(DriverOrderStatus.PICKED_UP_ORDER);
        }}
      />
      <ConfirmDeliverySheet
        open={showConfirm}
        order={order}
        onClose={() => setShowConfirm(false)}
        onConfirm={confirmDelivery}
      />
      <InfoAlert alert={alert} onClose={() => setAlert(null)} />
    </div>
  );
}
